import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';

const PersonalityAssessmentForm = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    personalityTraits: data.personalityTraits || {},
    entrepreneurialCharacteristics: data.entrepreneurialCharacteristics || {},
    communicationStyle: data.communicationStyle || {},
    riskTolerance: data.riskTolerance || {},
    leadershipStyle: data.leadershipStyle || {},
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const personalityQuestions = [
    {
      id: 'extroversion',
      question: 'I feel energized by social interactions and working with groups',
      category: 'personalityTraits',
    },
    {
      id: 'conscientiousness',
      question: 'I am highly organized and pay attention to details',
      category: 'personalityTraits',
    },
    {
      id: 'openness',
      question: 'I enjoy exploring new ideas and creative solutions',
      category: 'personalityTraits',
    },
    {
      id: 'agreeableness',
      question: 'I prefer collaboration over competition',
      category: 'personalityTraits',
    },
    {
      id: 'neuroticism',
      question: 'I handle stress and pressure well in challenging situations',
      category: 'personalityTraits',
    },
  ];

  const entrepreneurialQuestions = [
    {
      id: 'visionary',
      question: 'I can clearly articulate my business vision and inspire others',
      category: 'entrepreneurialCharacteristics',
    },
    {
      id: 'persistent',
      question: 'I persevere through setbacks and obstacles',
      category: 'entrepreneurialCharacteristics',
    },
    {
      id: 'opportunistic',
      question: 'I quickly identify and act on business opportunities',
      category: 'entrepreneurialCharacteristics',
    },
    {
      id: 'adaptive',
      question: 'I easily adjust my approach based on market feedback',
      category: 'entrepreneurialCharacteristics',
    },
    {
      id: 'resilient',
      question: 'I bounce back quickly from failures and learn from them',
      category: 'entrepreneurialCharacteristics',
    },
  ];

  const communicationQuestions = [
    {
      id: 'direct',
      question: 'I prefer direct, straightforward communication',
      category: 'communicationStyle',
    },
    {
      id: 'diplomatic',
      question: 'I carefully consider how my message will be received',
      category: 'communicationStyle',
    },
    {
      id: 'persuasive',
      question: 'I am skilled at convincing others of my ideas',
      category: 'communicationStyle',
    },
    {
      id: 'listener',
      question: 'I actively listen and seek to understand others first',
      category: 'communicationStyle',
    },
  ];

  const riskQuestions = [
    {
      id: 'riskTaker',
      question: 'I am comfortable making decisions with incomplete information',
      category: 'riskTolerance',
    },
    {
      id: 'calculated',
      question: 'I prefer to analyze risks thoroughly before acting',
      category: 'riskTolerance',
    },
    {
      id: 'innovative',
      question: 'I enjoy trying new approaches and experimenting',
      category: 'riskTolerance',
    },
    {
      id: 'conservative',
      question: 'I prefer proven methods and established practices',
      category: 'riskTolerance',
    },
  ];

  const leadershipQuestions = [
    {
      id: 'delegator',
      question: 'I prefer to delegate tasks and focus on strategy',
      category: 'leadershipStyle',
    },
    {
      id: 'handsOn',
      question: 'I like to be involved in day-to-day operations',
      category: 'leadershipStyle',
    },
    {
      id: 'mentor',
      question: 'I enjoy developing and coaching my team members',
      category: 'leadershipStyle',
    },
    {
      id: 'decisionMaker',
      question: 'I make decisions quickly and take responsibility for outcomes',
      category: 'leadershipStyle',
    },
  ];

  const handleResponseChange = (category, questionId, response) => {
    setAssessmentData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [questionId]: response,
      },
    }));
  };

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const categories = [
      'personalityTraits',
      'entrepreneurialCharacteristics',
      'communicationStyle',
      'riskTolerance',
      'leadershipStyle',
    ];

    categories.forEach(category => {
      const categoryData = assessmentData[category];
      Object.values(categoryData).forEach(response => {
        if (response === 'stronglyAgree') {
          totalPoints += 4;
        } else if (response === 'agree') {
          totalPoints += 3;
        } else if (response === 'neutral') {
          totalPoints += 2;
        } else if (response === 'disagree') {
          totalPoints += 1;
        }
        maxPoints += 4;
      });
    });

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = score => {
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 45) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 75) return 'Strong Entrepreneurial Profile';
    if (score >= 60) return 'Good Entrepreneurial Potential';
    if (score >= 45) return 'Developing Entrepreneurial Traits';
    return 'Needs Development';
  };

  const renderQuestionSection = (title, questions, category) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-6 text-xl font-semibold text-gray-900">{title}</h3>

      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="border-b border-gray-100 pb-4 last:border-b-0">
            <p className="mb-3 text-sm font-medium text-gray-900">
              {index + 1}. {question.question}
            </p>

            <div className="flex flex-wrap gap-2">
              {[
                {
                  value: 'stronglyAgree',
                  label: 'Strongly Agree',
                  icon: CheckCircleIcon,
                  color: 'green',
                },
                { value: 'agree', label: 'Agree', icon: CheckCircleIcon, color: 'blue' },
                { value: 'neutral', label: 'Neutral', icon: MinusCircleIcon, color: 'gray' },
                { value: 'disagree', label: 'Disagree', icon: XCircleIcon, color: 'red' },
              ].map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => handleResponseChange(category, question.id, value)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    assessmentData[category][question.id] === value
                      ? `bg-${color}-100 border-${color}-500 text-${color}-700`
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <UserIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Personality Assessment</h2>
            <p className="text-gray-600">
              Evaluate your personality traits, entrepreneurial characteristics, and leadership
              style
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      {renderQuestionSection('Personality Traits', personalityQuestions, 'personalityTraits')}

      {renderQuestionSection(
        'Entrepreneurial Characteristics',
        entrepreneurialQuestions,
        'entrepreneurialCharacteristics'
      )}

      {renderQuestionSection('Communication Style', communicationQuestions, 'communicationStyle')}

      {renderQuestionSection('Risk Tolerance & Innovation', riskQuestions, 'riskTolerance')}

      {renderQuestionSection('Leadership Style', leadershipQuestions, 'leadershipStyle')}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="Add any additional observations, strengths, or areas for development..."
        />
      </motion.div>
    </div>
  );
};

export default PersonalityAssessmentForm;
